import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import Modal from 'modal-enhanced-react-native-web';
import { Feather, Ionicons } from '@expo/vector-icons';
import { SHADOWS } from '../../constants/theme';

/* ── Contextual Icon Resolver ── */
const getIconForTitle = (title = '') => {
  const lower = title.toLowerCase();
  if (lower.includes('delete') || lower.includes('remove')) {
    return {
      icon: 'trash-2',
      iconColor: '#EF4444',
      bgColor: '#FEE2E2',
    };
  }
  if (lower.includes('archive') && !lower.includes('unarchive')) {
    return {
      icon: 'archive',
      iconColor: '#6C2BD9',
      bgColor: '#F0E8FF',
    };
  }
  if (lower.includes('unarchive')) {
    return {
      icon: 'refresh-cw',
      iconColor: '#10B981',
      bgColor: '#D1FAE5',
    };
  }
  if (lower.includes('warning') || lower.includes('alert')) {
    return {
      icon: 'alert-triangle',
      iconColor: '#F59E0B',
      bgColor: '#FEF3C7',
    };
  }
  return {
    icon: 'help-circle',
    iconColor: '#6C2BD9',
    bgColor: '#F5F3FF',
  };
};

/* ── Contextual Button Theme Resolver ── */
const getButtonStyle = (button, index, totalButtons) => {
  const text = (button.text || '').toLowerCase();
  const isDestructive = text.includes('delete') || text.includes('remove');
  const isCancel = text.includes('cancel') || text.includes('no') || text.includes('close');
  const isSecondary =
    isCancel ||
    (index > 0 && totalButtons > 1 && !isDestructive && text !== 'archive' && text !== 'unarchive');

  if (isDestructive) {
    return {
      bg: '#EF4444',
      text: '#FFFFFF',
      border: '#EF4444',
    };
  }
  if (isSecondary) {
    return {
      bg: '#FFFFFF',
      text: '#374151',
      border: '#E5E7EB',
    };
  }
  return {
    bg: button.color && button.color !== '#7C3CC4' && button.color !== '#6D08C0' ? button.color : '#6C2BD9',
    text: '#FFFFFF',
    border: button.color && button.color !== '#7C3CC4' && button.color !== '#6D08C0' ? button.color : '#6C2BD9',
  };
};

const PopUp = (props) => {
  const {
    modalVisible,
    titleText,
    customSubtitle,
    titleColor,
    subTitleText,
    subTitleColor,
    actionButtons,
    closePopup,
    hideClose,
  } = props;

  const iconInfo = getIconForTitle(titleText || '');

  return (
    <Modal
      isVisible={modalVisible}
      backdropTransitionInTiming={200}
      backdropTransitionOutTiming={200}
      backdropColor={'rgba(17, 24, 39, 0.4)'}
      backdropOpacity={1}
      onBackdropPress={() => closePopup && closePopup()}
      style={s.modalOverlay}
    >
      <View style={s.card}>
        {/* Top Close Button */}
        {!hideClose && (
          <TouchableOpacity
            style={s.closeBtn}
            onPress={() => closePopup && closePopup()}
            activeOpacity={0.7}
          >
            <Ionicons name="close" size={18} color="#6B7280" />
          </TouchableOpacity>
        )}

        {/* Icon Badge */}
        <View style={[s.iconBadge, { backgroundColor: iconInfo.bgColor }]}>
          <Feather name={iconInfo.icon} size={22} color={iconInfo.iconColor} />
        </View>

        {/* Title */}
        <Text style={s.title}>
          {titleText}
        </Text>

        {/* Subtitle / Description */}
        {subTitleText ? (
          <Text style={s.description}>
            {subTitleText}
          </Text>
        ) : null}

        {customSubtitle ? customSubtitle : null}

        {/* Action Buttons */}
        {actionButtons && actionButtons.length > 0 ? (
          <View style={s.actionRow}>
            {actionButtons.map((btn, i) => {
              const btnTheme = getButtonStyle(btn, i, actionButtons.length);
              return (
                <TouchableOpacity
                  key={i}
                  style={[
                    s.actionBtn,
                    {
                      backgroundColor: btnTheme.bg,
                      borderColor: btnTheme.border,
                    },
                  ]}
                  onPress={() => {
                    btn.onPress && btn.onPress();
                  }}
                  activeOpacity={0.8}
                >
                  <Text style={[s.actionBtnText, { color: btnTheme.text }]}>
                    {btn.text}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        ) : null}
      </View>
    </Modal>
  );
};

const s = StyleSheet.create({
  modalOverlay: {
    margin: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    width: '90%',
    maxWidth: 420,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    position: 'relative',
    ...SHADOWS.lg,
  },
  closeBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  iconBadge: {
    width: 50,
    height: 50,
    borderRadius: 25,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    marginTop: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    lineHeight: 21,
    color: '#4B5563',
    textAlign: 'center',
    marginBottom: 12,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    width: '100%',
    marginTop: 16,
  },
  actionBtn: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionBtnText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

PopUp.defaultProps = {
  titleColor: '#7C3CC4',
  subTitleColor: '#000000',
  hideClose: false,
};

export default PopUp;
